import React, { useCallback, useMemo } from 'react';

import Dexie from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';

import db, { DEXIE_IS_LOADING } from '../lib/db';
import { cn } from '../lib/utils';

import { KEYCHAIN } from '../contexts/KeyMaster';
import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import { DropdownMenu } from './shared/DropdownMenu';

export function AgentChatDropdown({ currentAgentChatId, onAgentChatSelect, onClose }) {
  const { workspaceId } = useRouteWorkspace();
  const agentChats = useLiveQuery(
    async function fetchAgentChats() {
      if (!workspaceId) return [];
      const rows = await db.agent_chats
        .where('[workspace_id+sort_order]')
        .between([workspaceId, Dexie.minKey], [workspaceId, Dexie.maxKey])
        .toArray();
      return rows.reverse();
    },
    [workspaceId],
    DEXIE_IS_LOADING,
  );

  // filter out chats without a title or titled "Untitled", unless it's the current agent chat
  const filteredChats = useMemo(
    function filterChats() {
      if (agentChats === DEXIE_IS_LOADING || !Array.isArray(agentChats)) return [];
      return agentChats
        .filter(function isMainUiChat(chat) {
          return (chat.kind || 'standard') === 'standard';
        })
        .filter(function hasValidTitle(chat) {
          return (chat.title && chat.title !== 'Untitled') || chat.id === currentAgentChatId;
        });
    },
    [agentChats, currentAgentChatId],
  );

  // Transform chats into dropdown items
  const items = useMemo(
    function buildItems() {
      return filteredChats.map(function chatToItem(chat) {
        return {
          value: chat.id,
          label: chat.title || 'Untitled',
          selected: chat.id === currentAgentChatId,
          status: chat.status,
        };
      });
    },
    [filteredChats, currentAgentChatId],
  );

  const renderItem = useCallback(function renderAgentChatItem({ item, isActive, isSelected, ref, onClick, onMouseMove }) {
    return (
      <button
        key={item.value}
        ref={ref}
        className={cn(
          'flex w-full cursor-pointer items-center gap-1.5 rounded-xl px-2 py-1.5 text-left text-xs/5',
          isSelected && 'font-semibold',
          isActive
            ? 'bg-gray-300/40 text-black dark:bg-neutral-700/50 dark:text-white'
            : isSelected
              ? 'text-black hover:bg-gray-300/40 dark:text-white dark:hover:bg-neutral-700/50'
              : 'text-gray-500 hover:bg-gray-300/40 dark:text-neutral-400 dark:hover:bg-neutral-700/50',
        )}
        onClick={onClick}
        onMouseMove={onMouseMove}
      >
        {item.status === 'running' && <div className="size-2 flex-shrink-0 rounded-full bg-green-500"></div>}
        {item.status === 'failed' && <div className="size-2 flex-shrink-0 rounded-full bg-red-500"></div>}
        <div>{item.label}</div>
      </button>
    );
  }, []);

  if (agentChats === DEXIE_IS_LOADING) return null;

  return (
    <DropdownMenu
      items={items}
      onSelect={onAgentChatSelect}
      onClose={onClose}
      keychainId={KEYCHAIN.POPOVER__AGENT_CHAT_DROPDOWN}
      renderItem={renderItem}
    />
  );
}
